import { PaymentProvider } from "./billing";
import { Condition } from "./condition";
import { LabExam } from "./labExam";
import { Calendar, Question, Questions } from "./questionnaire";
import { Address } from "./user";

export enum ShippingMethods {
  STANDARD = "standard",
  USPS_PRIORITY = "usps_priority",
  USPS_EXPRESS = "usps_priority_express",
  UPS_NEXT_DAY_AIR = "ups_next_day_air",
  UPS_SECOND_DAY_AIR = "ups_second_day_air",
  FEDX_GROUND = "fedex_ground",
  FEDX_STANDARD_OVERNIGHT = "fedex_standard_overnight",
  FEDX_PRIORITY_OVERNIGHT = "fedex_priority_overnight",
}

export enum CaseStatuses {
  CREATED = "created",
  ASSIGNED = "assigned",
  SUPPORT = "support",
  WAITING = "waiting",
  PROCESSING = "processing",
  COMPLETED = "completed",
  CANCELLED = "cancelled",
}

export enum OrderStatuses {
  NEW = "new",
  IN_PROGRESS = "in_progress",
  OUT_FOR_DELIVERY = "out_for_delivery",
  COMPLETED = "completed",
  CANCELLED = "cancelled",
  ERROR = "error",
}

export enum OrderCaseStatuses {
  PHYSICIAN_REVIEW = "physician review",
  PENDING_PAYMENT = "pending payment",
  DECLINED_PAYMENT = "Declined payment",
  PHARMACY_PROCESSING = "pharmacy processing",
  CANCELLED = "cancelled",
  PAUSED = "paused",
  SHIPPED = "shipped",
  COMPLETE = "complete",
}

export interface Replacement {
  replacement_reason?: string;
  status: OrderStatuses;
  carrier?: string;
  tracking_number?: string;
}

export interface InvoiceLineItem {
  description: string;
  amount: number;
}

// Failure markers on the snapshot of a declined HSA/FSA order, which has no vendor invoice.
export interface DeclinedInvoiceMetadata {
  isEstimate?: boolean;
  failedAt?: string;
  declineCode?: string;
  message?: string;
}

export interface Invoice {
  amount: number;
  lineItems: Array<InvoiceLineItem>;
  shippingAmount: number;
  metadata?: DeclinedInvoiceMetadata;
  provider?: PaymentProvider;
  paid?: boolean;
}
export interface Order {
  UserId: string;
  carrier: string;
  caseId: string;
  createdAt: string;
  id: number;
  order_id: string;
  status: string;
  paid?: boolean;
  externalStatus?: string;
  invoice?: Invoice | null;
  isInvoiceUnavailable?: boolean;
  // The patient is reviewing the card payment's amount before confirming the charge.
  isAwaitingCardConfirmation?: boolean;
  invoiceId?: string;
  tracking_number: string;
  shipping_method: string | null;
  departureDate: string;
  updatedAt: string;
  Case?: Case;
  replacement?: Replacement;
  // The numeric `id` of the Order that replaced this one, once it was reissued.
  supersededByOrderId?: number | null;
}

// An Order on a Case as listed by an admin's lookup of a User's Cases, newest first.
export interface CaseOrderSummary {
  id: number;
  order_id: string;
  status: string;
}

export type Trip = Calendar;

export interface Case {
  id?: number;
  caseId: string | null;
  conditions: Condition[];
  case_status: CaseStatuses | null;
  invoiceId: string | null;
  // Provider of the case's payment: "STRIPE" or "WITHFLEX" (HSA/FSA).
  paymentProvider?: string;
  invoice?: {
    id: string;
    hostedInvoiceUrl: string;
    paid: boolean;
  };
  case_questions: Question[];
  shipping_method: ShippingMethods | null;
  shippingAddress?: Address;
  CurexaOrder: Order;
  orders?: CaseOrderSummary[];
  case_assigment_id?: string;
  clinician?: {
    id: string;
    fullname: string;
    photo: string;
    specialty: string;
  };
  updatedAt: string;
  createdAt?: string;
  trip: Trip;
  UserId?: string | null;
  upsell?: RecommendedConditions;
  paid: boolean;
  totalQuestions: number;
  labExams?: LabExam[];
}

export type CaseUpdate = Omit<
  Partial<Case>,
  | "updatedAt"
  | "createdAt"
  | "paid"
  | "invoice"
  | "CurexaOrder"
  | "case_assigment_id"
  | "clinician"
  | "shippingAddress"
> & {
  /**
   * We don't use AddressUpdate because we want to update the address id
   * in the case entity and not updating the address itself.
   *
   * As such, this shippingAddress handling is a case update,
   * refering to an specific shipping address and not an address update.
   */
  shippingAddress?: { id: string };
};

export interface CaseUpdateInput extends Omit<CaseUpdate, "case_questions"> {
  questions?: Questions;
}

export interface Attachment {
  name: string;
  mime_type: string;
  url: string;
  url_thumbnail: string;
  file_id: string;
}

export interface CaseNotification {
  event_type: string;
  case_id: string;
  metadata: "";
}

export interface RecommendedConditions {
  conditions: Condition[];
  reason?: string;
}
